import { Observer } from 'mobx-react';
import React from 'react';
import { idGenerator } from '~/lib/helper/IdHelper';

const groupNameGen = idGenerator('selectableGroup');

type SelectableGridProps = {
    // Visually-hidden label announced for the radiogroup (rendered as the <legend>).
    legend: string;
    // Header-row cells (each a block element, e.g. <div>). Rendered inside an
    // aria-hidden container: the per-cell `.th` labels carry the a11y column
    // context, so the visual header must not be announced twice.
    header: React.ReactNode;
    // Extra class on the grid element; consumers set --selectable-grid-columns
    // (and any grid-scoped styles) against it.
    gridClassName?: string;
    // Rows, given the generated per-instance radio-group name to put on each
    // <input type="radio"> so one page can hold several independent groups.
    children: (groupName: string) => React.ReactNode;
    // Wrap rows in an <Observer> so observable reads in the row closure are tracked.
    // Prop-only lists pass false to skip MobX's dev-only reactionRequiresObservable
    // warning; defaults true so omitting it can't cause a stale row.
    observe?: boolean;
};

/**
 * Owns the shared `.selectable-grid` scaffold — a <fieldset> with a visually-hidden <legend>, the
 * aria-hidden header row, and a per-instance radio group name — so a row-selectable list only has to
 * render its rows and set the column tracks. Keeps the a11y-critical structure in one place rather than
 * hand-copied per component. Rows are native radios, which supply group semantics, arrow-key navigation,
 * selection state, disabled handling and the single tab stop without a roving tabindex or `aria-checked`.
 *
 * Each row is a `<label className="radio-row">` whose first labelable descendant is the radio, so the whole
 * row text becomes the radio's accessible name (e.g. "4 cores, 2 GB, 20 GB, 2000 GB, $36.32"). Some rows
 * nest their own controls (`SizeSelector`'s inline selects, `BackupLabelEdit`'s form), which the HTML
 * `<label>` content model disallows. That is deliberate: browsers operate the nested control without
 * toggling the radio, and a `<div>` row labelled through `htmlFor` would shrink the radio's name to the
 * title alone.
 *
 * `.selectable-grid` (`Theme.scss`) is a CSS grid at `lg` and above. Below `lg` each row collapses into a
 * card (`selectable-grid-collapsed`): `.cell-title` becomes the header band, the per-cell `.th` labels
 * appear, and `collapsed-only` / `collapsed-hidden` / `truncate-md` behave as under `.table-collapse`.
 * `.radio-row.disabled` / `.radio-row.invalid` only swap an unselected row's radio for the unavailable
 * mark; each consumer greys the row itself with its own `.radio-row.disabled` / `.invalid > .cell` rule
 * (`SizeSelector.scss`, `Clone.scss`).
 */
export default class SelectableGrid extends React.Component<SelectableGridProps> {
    private readonly groupName = groupNameGen();

    render() {
        const { legend, header, gridClassName, children, observe = true } = this.props;
        return (
            <fieldset className="selectable-fieldset">
                <legend className="visually-hidden">{legend}</legend>
                <div className={`selectable-grid ${gridClassName ?? ''}`}>
                    <div className="grid-header" aria-hidden="true">
                        {header}
                    </div>
                    {observe ? <Observer>{() => <>{children(this.groupName)}</>}</Observer> : children(this.groupName)}
                </div>
            </fieldset>
        );
    }
}
